import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  useTheme
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useGarage } from '../contexts/GarageContext';
import GarageSettingsModal from '../components/system-management/GarageSettingsModal';
import ReportsTab from '../components/garage-admin/ReportsTab';
import Calendar from '../components/calendar/Calendar';
import Appointments from '../components/garage-admin/Appointments';

const GarageAdminDashboard = () => {
  const location = useLocation();
  const theme = useTheme();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const { user } = useAuth();
  const { selectedGarage, loading, error } = useGarage();

  // Get view from URL parameters, default to calendar
  const getActiveView = () => {
    const params = new URLSearchParams(location.search);
    return params.get('view') || 'calendar';
  };
  
  const [activeView, setActiveView] = useState(getActiveView());

  // Update view when URL changes
  useEffect(() => {
    setActiveView(getActiveView());
  }, [location.search]);

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  const getPageInfo = () => {
    switch (activeView) {
      case 'appointments':
        return {
          title: 'Appointments',
          description: 'Manage customer appointments, schedules, and booking status'
        };
      case 'calendar':
        return {
          title: 'Calendar',
          description: 'View and manage MOT test appointments, availability slots, and booking schedules'
        };
      case 'configuration':
        return {
          title: 'Configuration',
          description: 'Configure garage settings, opening hours, services, and pricing rules'
        };
      case 'reports':
        return {
          title: 'Reports',
          description: 'Monitor booking performance, revenue analytics, and operational insights'
        };
      default:
        return {
          title: 'Calendar',
          description: 'View and manage MOT test appointments, availability slots, and booking schedules'
        };
    }
  };

  if (user?.role !== 'garage_admin') {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" color="error" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You need garage admin privileges to access this page.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography>Loading...</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'flex-start' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h3" component="h1" gutterBottom>
                {getPageInfo().title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {getPageInfo().description}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        {selectedGarage && (
          <>
            {activeView === 'appointments' && (
              <Appointments />
            )}
            
            {activeView === 'calendar' && (
              <Calendar />
            )}
            
            {activeView === 'configuration' && (
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <BusinessIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {selectedGarage.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          const addressParts = [
                            selectedGarage.address_line1,
                            selectedGarage.address_line2,
                            selectedGarage.address_line3,
                            selectedGarage.town,
                            selectedGarage.postcode
                          ].filter(Boolean);
                          return addressParts.length > 0 ? addressParts.join(', ') : 'No address';
                        })()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedGarage.phone || 'No phone number'}
                      </Typography>
                    </Box>
                    <Chip
                      icon={selectedGarage.is_active ? <ActiveIcon /> : <InactiveIcon />}
                      label={selectedGarage.is_active ? 'Active' : 'Inactive'}
                      color={selectedGarage.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SettingsIcon />}
                      onClick={() => setSettingsModalOpen(true)}
                      sx={{ 
                        alignSelf: 'flex-end'
                      }}
                    >
                      Settings
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {activeView === 'reports' && (
              <ReportsTab />
            )}
          </>
        )}
      </Container>
      
      {/* Settings Modal */}
      <GarageSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        garage={selectedGarage}
      />
    </>
  );
};












export default GarageAdminDashboard;
